import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Claim } from '@wsphere/warranties/domain';
import { BehaviorSubject } from 'rxjs';
import { CLAIM_ITEM_CONTROLS_CONFIG } from '../../config/claim-item-controls.config';
import { ClaimItemHoldService, ClaimItemLineService, ClaimService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-lines-table',
  templateUrl: './claim-item-lines-table.component.html',
  standalone: false,
})
export class ClaimItemLinesTableComponent {
  constructor(
    private lineSvc: ClaimItemLineService,
    private claimSvc: ClaimService,
    private holdSvc: ClaimItemHoldService
  ) {}

  readonly config = CLAIM_ITEM_CONTROLS_CONFIG;

  @Input() set claim(claim: Claim.Model | null) {
    this._claim = claim;
    this.refresh();
  }
  get claim() {
    return this._claim;
  }
  private _claim: Claim.Model | null = null;

  @Input() set item(item: Claim.ClaimItem.Model | null) {
    this._item = item;
    this.refresh();
  }
  get item() {
    return this._item;
  }
  private _item: Claim.ClaimItem.Model | null = null;

  /** Only hides the buttons; the server checks too. */
  @Input() canManage = false;

  /** Shows the adjust action; the server checks too. */
  @Input() canAdjust = false;

  /** Staff without adjust rights can still read a charge's recommendation history. */
  @Input() canViewHistory = false;

  /** A charge was saved or removed, which can close its holds. */
  @Output() linesChanged = new EventEmitter<void>();

  readonly lines$ = new BehaviorSubject<Claim.ClaimItem.Line.Model[]>([]);

  /** Charges with an open evidence hold. */
  onHold = new Set<string>();

  /** The tenant's units, for "1 Hour" rather than "1 Hours". */
  units: Claim.ClaimItem.Line.Units = { units: [] };

  async refresh() {
    if (!this._claim || !this._item) return;

    const [lines, holds, units] = await Promise.all([
      this.lineSvc.list(this._claim.id, this._item.id).catch(() => null),
      this.holdSvc.holds(this._claim.id, this._item.id).catch(() => null),
      this.lineSvc.units().catch(() => null),
    ]);

    if (units) this.units = units;

    this.onHold = new Set(
      (holds?.items ?? [])
        .filter(hold => hold.status === Claim.ClaimItem.Hold.Status.OPEN && hold.claimItemLineId)
        .map(hold => hold.claimItemLineId as string)
    );
    this.lines$.next(lines?.items ?? []);
  }

  /** A charge was saved or removed: reload the rows, and the claim totals once they catch up. */
  async changed() {
    this.claimSvc.refreshClaimTotals(true);
    await this.refresh();
    this.linesChanged.emit();
  }

  showActions() {
    return this.canManage || this.canAdjust || this.canViewHistory;
  }

  requestedTotal(lines: Claim.ClaimItem.Line.Model[]) {
    return lines.reduce((sum, line) => sum + (line.deleted ? 0 : line.requestedAmount), 0);
  }

  authorizedTotal(lines: Claim.ClaimItem.Line.Model[]) {
    return lines.reduce((sum, line) => sum + (line.deleted ? 0 : (line.authorizedAmount ?? 0)), 0);
  }

  difference(line: Claim.ClaimItem.Line.Model) {
    return line.authorizedAmount === null ? null : line.requestedAmount - line.authorizedAmount;
  }

  unitLabel(line: Claim.ClaimItem.Line.Model) {
    return Claim.ClaimItem.Line.unitLabelFor(this.units, line) ?? '';
  }

  formatAmount(amount: number | null | undefined) {
    return amount === null || amount === undefined ? '-' : '$' + (amount / 100).toFixed(2);
  }
}
